import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {ConfirmDialogConfig, ConfirmDialogRef, ConfirmDialogResult} from '../model/confirm-dialog.model';

@Injectable({providedIn: 'root'})
export class ConfirmDialogService {
  readonly config$ = new Subject<ConfirmDialogConfig>();

  open(config: ConfirmDialogConfig): ConfirmDialogRef {
    const closeSubject = new Subject<ConfirmDialogResult | undefined>();

    const wrappedConfig: ConfirmDialogConfig = {
      ...config,
      accept: () => {
        config.accept?.();
        closeSubject.next({accepted: true});
        closeSubject.complete();
      },
      reject: () => {
        config.reject?.();
        closeSubject.next({accepted: false});
        closeSubject.complete();
      },
    };

    this.config$.next(wrappedConfig);

    return {
      onClose: closeSubject.asObservable(),
      close: (result?: ConfirmDialogResult) => {
        if (result?.accepted) {
          wrappedConfig.accept?.();
        } else {
          wrappedConfig.reject?.();
        }
      },
    };
  }

  public confirmDelete(message?: string, onAccept?: () => void): ConfirmDialogRef {
    return this.open({
      message: message ?? 'Are you sure you want to delete this item?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      severity: 'danger',
      defaultFocus: 'reject',
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
      },
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      accept: onAccept,
    });
  }

  public confirmWarning(message?: string, onAccept?: () => void): ConfirmDialogRef {
    return this.open({
      message: message ?? 'Please confirm to proceed.',
      header: 'Warning',
      icon: 'pi pi-exclamation-triangle',
      severity: 'warn',
      defaultFocus: 'reject',
      acceptButtonProps: {
        label: 'Yes',
        severity: 'warn',
      },
      rejectButtonProps: {
        label: 'No',
        severity: 'secondary',
        outlined: true,
      },
      accept: onAccept,
    });
  }

  public confirmAsync(config: ConfirmDialogConfig): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.open({
        ...config,
        accept: () => {
          config.accept?.();
          resolve(true);
        },
        reject: () => {
          config.reject?.();
          resolve(false);
        },
      });
    });
  }
}
